import { useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { InputViewModel } from '@sitecore-jss/sitecore-jss-forms';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';

import { useGoogleRecaptchaManager } from '../../../contexts/GoogleRecaptchaManagerContext';

const REPLACEMENT_CHAR = '_';

/**
 * Sanitizes a form name to be used with Google reCAPTCHA v3.
 *
 * This function transforms a string into a format suitable for Google reCAPTCHA by:
 * 1. Replacing spaces with underscores
 * 2. Replacing any non-alphabetical characters (except for underscores and forward slashes) with underscores
 * 3. Reducing multiple consecutive underscores to a single underscore
 *
 * @param name - The original form name to sanitize
 * @returns A sanitized string that can be safely used with Google reCAPTCHA v3
 */
const sanitizeFormNameForRecaptcha = (name: string): string =>
  name
    .replace(/\s+/g, REPLACEMENT_CHAR)
    .replace(/[^A-Za-z\/_]/g, REPLACEMENT_CHAR)
    .replace(/__+/g, REPLACEMENT_CHAR);

const GoogleRecaptchaV3Input: React.FC<ValueFieldProps<InputViewModel>> = (props) => {
  const { field, fieldValidationErrorsComponent, isValid, onChange, errors } = props;
  const { id, name } = field.valueField;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const {
    refreshRecaptchaToken,
    refreshRequestId,
    refreshValidationErrorFields,
    registerRefreshRecaptchaHandler,
    formName,
  } = useGoogleRecaptchaManager();
  const [token, setToken] = useState('');
  const latestFieldState = useRef({ name, formName, isValid, errors });
  const lastHandledRefreshRequestId = useRef(0);
  const clearErrorsOnNextRefresh = useRef(false);

  useEffect(() => {
    latestFieldState.current = { name, formName, isValid, errors };
  }, [name, formName, isValid, errors]);

  useEffect(() => {
    if (!refreshRequestId || lastHandledRefreshRequestId.current === refreshRequestId) return;

    lastHandledRefreshRequestId.current = refreshRequestId;
    clearErrorsOnNextRefresh.current = !refreshValidationErrorFields.includes(
      latestFieldState.current.name
    );
    refreshRecaptchaToken();
  }, [refreshRecaptchaToken, refreshRequestId, refreshValidationErrorFields]);

  useEffect(() => {
    if (!executeRecaptcha) return;

    const getRecaptchaToken = async () => {
      const {
        formName: currentFormName,
        name: currentName,
        isValid: currentIsValid,
        errors: currentErrors,
      } = latestFieldState.current;

      const sanitizedFormName = sanitizeFormNameForRecaptcha(currentFormName);
      const receivedToken = await executeRecaptcha(sanitizedFormName);
      const shouldClearErrors = clearErrorsOnNextRefresh.current;

      clearErrorsOnNextRefresh.current = false;
      setToken(receivedToken);
      onChange(
        currentName,
        receivedToken,
        shouldClearErrors ? true : currentIsValid,
        shouldClearErrors ? [] : currentErrors
      );
    };

    getRecaptchaToken();
    registerRefreshRecaptchaHandler(getRecaptchaToken);

    // Make sure reCAPTCHA token is fresh since its timeout is 2 minutes
    const refreshTokenInterval = setInterval(getRecaptchaToken, 90 * 1000);
    return () => clearInterval(refreshTokenInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeRecaptcha]);

  const FieldValidationErrors = fieldValidationErrorsComponent;

  return (
    <>
      <input id={id} name={name} type="hidden" value={token} />
      {!!FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};
export default GoogleRecaptchaV3Input;
